import React, { useCallback, useRef, useState, useEffect } from 'react';
import Toolbar from './toolbar/toolbar';
import { cn } from './_lib/utils';
import Flipbook from './flipbook/flipbook';
import screenfull from 'screenfull';
import { TransformWrapper } from 'react-zoom-pan-pinch';
import { Document } from 'react-pdf';
import PdfLoading from './pad-loading/pdf-loading';
import useScreenSize from './_hooks/useScreensize';
import { pdfjs } from 'react-pdf';

// Critical: Set worker BEFORE anything else
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Aggressive polyfill for iOS and older browsers
if (typeof Promise.withResolvers === 'undefined') {
  Promise.withResolvers = function () {
    let resolve, reject;
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}

// iOS-specific configuration
const isIOSBrowser = () => {
  if (typeof window === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

// Disable CORS mode on iOS for better compatibility
if (isIOSBrowser()) {
  pdfjs.GlobalWorkerOptions.disableWorker = false;
}

const FlipbookViewer = ({ pdfUrl, className, disableShare }) => {
  const containerRef = useRef();
  const flipbookRef = useRef();
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfDetails, setPdfDetails] = useState(null);
  const [viewerStates, setViewerStates] = useState({ currentPageIndex: 0, zoomScale: 1 });
  const [loadProgress, setLoadProgress] = useState(0);
  const [renderError, setRenderError] = useState(null);
  const [isIOS] = useState(isIOSBrowser);

  useEffect(() => {
    // Log for debugging iOS issues
    if (isIOS) {
      console.log('🍎 iOS detected:', {
        userAgent: navigator.userAgent,
        viewport: document.querySelector('meta[name="viewport"]')?.content,
        windowHeight: window.innerHeight,
        windowWidth: window.innerWidth,
        devicePixelRatio: window.devicePixelRatio
      });
    }
  }, [isIOS]);

  const onDocumentLoadSuccess = useCallback(async (document) => {
    try {
      const pageDetails = await document.getPage(1);
      const pdfData = {
        totalPages: document.numPages,
        width: pageDetails.view[2],
        height: pageDetails.view[3]
      };
      
      if (isIOS) {
        console.log('✅ PDF loaded successfully on iOS:', pdfData);
      }
      
      setPdfDetails(pdfData);
      setPdfLoading(false);
      setLoadProgress(100);
      setRenderError(null);
      
      // Force a layout reflow on iOS to ensure proper rendering
      if (isIOS && containerRef.current) {
        setTimeout(() => {
          // eslint-disable-next-line no-unused-expressions
          void (containerRef.current?.offsetHeight);
          // Trigger repaint
          containerRef.current.style.display = 'none';
          setTimeout(() => {
            if (containerRef.current) {
              containerRef.current.style.display = 'flex';
            }
          }, 50);
        }, 100);
      }
    } catch (error) {
      console.error('Error loading document:', error);
      setRenderError(`Failed to load PDF: ${error.message}`);
      setPdfLoading(false);
    }
  }, [isIOS]);

  const onLoadProgress = useCallback((progressEvent) => {
    // react-pdf emits { loaded, total } if available
    const loaded = progressEvent?.loaded ?? progressEvent?.loadedBytes ?? 0;
    const total = progressEvent?.total ?? progressEvent?.totalBytes ?? 0;
    const pct = total ? Math.round((loaded / total) * 100) : 0;
    setLoadProgress(pct);
    
    if (isIOS && pct % 25 === 0 && pct > 0) {
      console.log(`📥 PDF loading: ${pct}%`);
    }
  }, [isIOS]);

  const onDocumentLoadError = useCallback((error) => {
    console.error('PDF Document load error:', error);
    const errorMsg = error?.message || 'Unable to load the PDF';
    setRenderError(`Error: ${errorMsg}. Please try refreshing the page.`);
    setPdfLoading(false);
    
    if (isIOS) {
      console.error('❌ iOS PDF loading failed:', error);
    }
  }, [isIOS]);

  const { width: screenWidth } = useScreenSize();
  const isMobile = screenWidth < 768;

  // Ensure viewport is properly set for iOS
  useEffect(() => {
    if (isIOS) {
      const meta = document.querySelector('meta[name="viewport"]');
      if (meta) {
        meta.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=5, user-scalable=yes, viewport-fit=cover');
      }
      
      // Force body to take full viewport on iOS
      document.body.style.position = 'fixed';
      document.body.style.width = '100vw';
      document.body.style.height = '100vh';
      document.body.style.overflow = 'hidden';
    }
  }, [isIOS]);

  return (
    <div 
      ref={containerRef} 
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%', 
        background: '#000', 
        height: '100vh',
        height: '100dvh',
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        WebkitUserSelect: 'none',
        userSelect: 'none',
        overflow: 'hidden',
        WebkitTouchCallout: 'none'
      }} 
      className={cn('', className)}
    >
      {pdfLoading && <PdfLoading progress={loadProgress} />}
      {renderError && (
        <div style={{ 
          position: 'fixed', 
          inset: 0, 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center', 
          background: '#000',
          color: '#fff',
          padding: '20px',
          textAlign: 'center',
          zIndex: 10,
          gap: '20px'
        }}>
          <div style={{ fontSize: '18px', fontWeight: 'bold' }}>⚠️ Error Loading Magazine</div>
          <div style={{ fontSize: '14px', opacity: 0.8 }}>{renderError}</div>
          <button 
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 20px',
              background: '#2a7398',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Retry
          </button>
        </div>
      )}
      <Document 
        file={pdfUrl} 
        onLoadSuccess={onDocumentLoadSuccess} 
        onLoadProgress={onLoadProgress}
        onLoadError={onDocumentLoadError}
        loading={<></>}
      >
        {(pdfDetails && !pdfLoading && !renderError) && (
          <TransformWrapper
            doubleClick={{ disabled: true }}
            pinch={{ step: 2 }}
            disablePadding={viewerStates?.zoomScale <= 1}
            initialScale={1}
            minScale={1}
            maxScale={5}
            onTransformed={({ state }) => setViewerStates({ ...viewerStates, zoomScale: state.scale })}
            wrapperStyle={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', WebkitTouchCallout: 'none' }}
            contentStyle={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'center' : 'stretch', justifyContent: isMobile ? 'center' : 'flex-start' }}
          >
            <div style={{ width: '100%', background: '#000', display: 'flex', flexDirection: 'column', flex: 1 }}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: isMobile ? 'center' : 'flex-start' }}>
                <Flipbook viewerStates={viewerStates} setViewerStates={setViewerStates} flipbookRef={flipbookRef} pdfDetails={pdfDetails} />
              </div>
              <div>
                <Toolbar viewerStates={viewerStates} setViewerStates={setViewerStates} containerRef={containerRef} flipbookRef={flipbookRef} screenfull={screenfull} pdfDetails={pdfDetails} disableShare={disableShare} />
              </div>
            </div>
          </TransformWrapper>
        )}
      </Document>
    </div>
  );
};

export default FlipbookViewer;
